import { z } from "zod";
import { zEmail, zId, zIsoDateTime } from "./common.js";

/**
 * CT-RM-20 (CT-INT-001 / CT-INT-006): app cards on records and partner
 * timeline events.
 *
 * App cards are DECLARATIVE. A partner registers a card (title, locations, an
 * https data URL); CauseTide fetches the card's data server-side with a signed
 * request, validates it against `zAppCardPayload`, and renders it with its own
 * components. No partner script or iframe ever runs inside CauseTide.
 */

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/** absolute https URL without embedded credentials */
export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

export const zHttpsUrl = z
  .string()
  .trim()
  .max(2000)
  .refine(isHttpsUrl, "must be an absolute https:// URL without credentials");

// ---------------------------------------------------------------------------
// card payload — what a partner's data URL returns (the SDK card schema)
// ---------------------------------------------------------------------------

export const APP_CARD_BADGE_TONES = ["neutral", "accent", "success", "warning", "danger", "info"] as const;
export type AppCardBadgeTone = (typeof APP_CARD_BADGE_TONES)[number];

const text = (max: number) => z.string().trim().min(1).max(max);

export const zAppCardItem = z.discriminatedUnion("type", [
  z.object({ type: z.literal("keyValue"), label: text(80), value: z.string().trim().max(500) }).strict(),
  z.object({ type: z.literal("text"), text: text(2000) }).strict(),
  z.object({ type: z.literal("link"), label: text(120), url: zHttpsUrl }).strict(),
  z.object({ type: z.literal("badge"), label: text(60), tone: z.enum(APP_CARD_BADGE_TONES).default("neutral") }).strict(),
]);
export type AppCardItem = z.infer<typeof zAppCardItem>;

export const zAppCardSection = z
  .object({
    title: z.string().trim().max(120).optional(),
    items: z.array(zAppCardItem).max(25),
  })
  .strict();
export type AppCardSection = z.infer<typeof zAppCardSection>;

/** a button that opens a partner https URL in a new tab — never runs code in CauseTide */
export const zAppCardAction = z.object({ label: text(60), url: zHttpsUrl }).strict();
export type AppCardAction = z.infer<typeof zAppCardAction>;

export const zAppCardPayload = z
  .object({
    version: z.literal(1).default(1),
    sections: z.array(zAppCardSection).max(10),
    actions: z.array(zAppCardAction).max(5).default([]),
  })
  .strict();
export type AppCardPayload = z.infer<typeof zAppCardPayload>;
export type AppCardPayloadInput = z.input<typeof zAppCardPayload>;

// ---------------------------------------------------------------------------
// card registration (by the partner, with its OAuth or API token)
// ---------------------------------------------------------------------------

export const APP_CARD_LOCATIONS = ["constituent", "gift"] as const;
export type AppCardLocation = (typeof APP_CARD_LOCATIONS)[number];
export const zAppCardLocation = z.enum(APP_CARD_LOCATIONS);

export const zAppCardKey = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, "lowercase letters, digits, - and _ only");

export const zRegisterAppCard = z
  .object({
    /** the partner's stable id for this card; registering the same key again updates it */
    key: zAppCardKey,
    title: text(80),
    locations: z.array(zAppCardLocation).min(1).max(APP_CARD_LOCATIONS.length),
    dataUrl: zHttpsUrl,
  })
  .strict();
export type RegisterAppCard = z.infer<typeof zRegisterAppCard>;

export const zUpdateAppCard = zRegisterAppCard.omit({ key: true }).partial().strict();
export type UpdateAppCard = z.infer<typeof zUpdateAppCard>;

/** admin toggle (Settings > Apps > Record cards) */
export const zSetAppCardEnabled = z.object({ enabled: z.boolean() }).strict();

export type AppCardSourceKind = "oauth_app" | "api_token";

export interface AppCardSource {
  kind: AppCardSourceKind;
  name: string;
  iconUrl: string | null;
}

export interface AppCardSummary {
  id: string;
  key: string;
  title: string;
  locations: AppCardLocation[];
  dataUrl: string;
  source: AppCardSource;
  /** off until an admin turns it on */
  enabled: boolean;
  /** false when the registering token was revoked or the app's authorization was withdrawn */
  active: boolean;
  /** e.g. "ctcs_ab12…" — never the secret itself */
  secretHint: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegisteredAppCard {
  card: AppCardSummary;
  /** HMAC signing secret; returned once, on first registration or rotation */
  signingSecret: string | null;
}

/**
 * One card as rendered on a record. `status: "unavailable"` covers timeouts,
 * network errors, non-2xx answers, oversized bodies and schema failures — the
 * UI shows a calm fallback; the reason stays server-side.
 */
export type AppCardRender =
  | { cardId: string; title: string; source: AppCardSource; status: "ok"; data: AppCardPayload; fetchedAt: string }
  | { cardId: string; title: string; source: AppCardSource; status: "unavailable"; fetchedAt: string };

/**
 * The signed card-data request CauseTide sends to `dataUrl`:
 *
 *   GET {dataUrl}?location=constituent&recordId=<uuid>&organizationId=<uuid>&cardKey=<key>
 *   X-CauseTide-Timestamp: unix seconds
 *   X-CauseTide-Nonce:     random, single use
 *   X-CauseTide-Signature: "sha256=" + hex HMAC-SHA256(signingSecret, `${timestamp}.${nonce}.GET.${fullUrl}`)
 *
 * Answer within 5 s with JSON matching `zAppCardPayload` (max 64 KB).
 */
export const APP_CARD_HEADERS = {
  timestamp: "x-causetide-timestamp",
  nonce: "x-causetide-nonce",
  signature: "x-causetide-signature",
  cardKey: "x-causetide-card",
} as const;
export const APP_CARD_TIMEOUT_MS = 5_000;
export const APP_CARD_MAX_BYTES = 64 * 1024;

export function appCardSigningBase(timestamp: string, nonce: string, method: string, url: string): string {
  return `${timestamp}.${nonce}.${method.toUpperCase()}.${url}`;
}

// ---------------------------------------------------------------------------
// partner timeline events
// ---------------------------------------------------------------------------

/** exactly one way to find the constituent */
export const zTimelineEventConstituentMatch = z
  .object({
    id: zId.optional(),
    email: zEmail.optional(),
    /** a value in the constituent's source ids (e.g. from an import); `externalSystem` narrows the match */
    externalId: z.string().trim().min(1).max(200).optional(),
    externalSystem: z.string().trim().min(1).max(64).optional(),
  })
  .strict()
  .refine((m) => [m.id, m.email, m.externalId].filter((v) => v !== undefined).length === 1, "Give exactly one of id, email or externalId")
  .refine((m) => m.externalSystem === undefined || m.externalId !== undefined, "externalSystem needs externalId");
export type TimelineEventConstituentMatch = z.infer<typeof zTimelineEventConstituentMatch>;

export const zTimelineEventSource = z
  .object({
    /** shown on the app badge; OAuth apps always show their registered name */
    name: text(80).optional(),
    iconUrl: zHttpsUrl.optional(),
  })
  .strict();

export const zCreateTimelineEvent = z
  .object({
    constituent: zTimelineEventConstituentMatch,
    title: text(200),
    summary: z.string().trim().max(2000).optional(),
    occurredAt: zIsoDateTime,
    /** deep link back into the partner app; opens in a new tab */
    url: zHttpsUrl.optional(),
    source: zTimelineEventSource.optional(),
    /** same as the Idempotency-Key header; the header wins when both are sent */
    idempotencyKey: z.string().trim().min(1).max(255).optional(),
  })
  .strict();
export type CreateTimelineEvent = z.infer<typeof zCreateTimelineEvent>;

export const zUpdateTimelineEvent = z
  .object({
    title: text(200).optional(),
    summary: z.string().trim().max(2000).nullable().optional(),
    occurredAt: zIsoDateTime.optional(),
    url: zHttpsUrl.nullable().optional(),
    source: zTimelineEventSource.optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, "Nothing to update");
export type UpdateTimelineEvent = z.infer<typeof zUpdateTimelineEvent>;

/** what the timeline entry's metadata carries for kind "app" */
export interface AppTimelineMetadata {
  app: { name: string; iconUrl: string | null };
  url: string | null;
}

export interface PartnerTimelineEvent {
  id: string;
  constituentId: string;
  title: string;
  summary: string | null;
  occurredAt: string;
  url: string | null;
  source: { name: string; iconUrl: string | null };
  idempotencyKey: string | null;
}
